import * as React from 'react'
import moment from 'moment'
import type { Moment } from 'moment'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import useActivityService from '@/modules/drivers/services/ActivityService'
import { useQueryCountries } from '@/modules/shared/query-client/useQueryCountries'
import useBusinessTripService from '@/modules/drivers/services/BusinessTripService'
import { EventModel } from '@/app/models/newDelegation/eventModel'
import { TripEventType } from '@/app/constants/delegation/tripEventType'
import { TripGenerationMode } from '@/app/constants/delegation/tripGenerationMode'
import { toast } from '@/lib/toasts/api'
import {
  HOMELAND,
  toggleRestOnBaseEvents,
} from '@/modules/drivers/utils/restOnBase'
import {
  addTripEvent,
  editTripEvent,
  removeTripEvents,
} from '@/modules/drivers/utils/eventListEdits'

import {
  ensureUid,
  ensureUids,
  matchEvent,
} from '@/app/common/newDelegation/eventIdentity'
import { DndRoot } from '@/modules/shared/components/DndRoot'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { CustomDragLayer } from '@/modules/drivers/components/businessTripChart'
import BusinessTripChartWrapperNew from '@/modules/drivers/tabs/businessTrips/addBusinessTrip/BusinessTripChartWrapperNew'
import { ActivityInfo, ActivityModel } from '@/modules/drivers/types/activities'
import useDriverActivityService from '@/modules/drivers/services/DriverActivityService'

import BusinessTripActionBar from '../../../components/BusinessTripActionBar'
import BusinessTripChartNavigation from './BusinessTripChartNavigation'
import { useEditBusinessTrip } from '../../../context/EditBusinessTripContext'

interface BusinessTripEditActivityChartProps {
  tripData: any
  onUpdate: (field: string, value: any) => void
  onUpdateArrayItem?: (arrayName: string, index: number, updates: any) => void
  onAddArrayItem?: (arrayName: string, newItem: any) => void
  onRemoveArrayItems?: (arrayName: string, indices: number[]) => void
  companyId: string
  driverId: string
  tripId: string
  onShowGpsView?: () => any
  onShowTmsView?: () => void
  onShowTachographView?: () => void
  onShowCardLoads?: () => void
}

const BusinessTripEditActivityChart: React.FC<
  BusinessTripEditActivityChartProps
> = ({
  tripData,
  onUpdate,

  companyId,
  driverId,
  tripId,
  onShowGpsView,
  onShowTmsView,
  onShowTachographView,
  onShowCardLoads,
}) => {
  const { t } = useLocalize()
  const { getDriverActivitiesForTrip } = useActivityService()
  const { data: countries = [] } = useQueryCountries({ companyId })
  const { getCountriesWithMinimalPayment, generateTrips } =
    useBusinessTripService()
  const { isOpenSettings, isOpenSummary, setShowBordersView } =
    useEditBusinessTrip()
  const [isRestsOnBaseModeOn, setIsRestsOnBaseModeOn] = React.useState(false)
  const rawEvents = tripData?.events || []

  // Ensure all events have stable unique IDs for matching
  const events = React.useMemo(() => {
    const withUids = ensureUids(rawEvents)
    // If we had to assign new UIDs, push them back to parent
    if (withUids !== rawEvents) {
      // Use microtask to avoid updating during render
      queueMicrotask(() => onUpdate('events', withUids))
    }
    return withUids
  }, [rawEvents])

  const [countriesWithMinimalPayment, setCountriesWithMinimalPayment] =
    React.useState<string[]>([])

  React.useEffect(() => {
    const fetchCountriesWithMinimalPayment = async () => {
      try {
        const data = await getCountriesWithMinimalPayment({ companyId })
        setCountriesWithMinimalPayment(data)
      } catch (err) {
        console.error('Error fetching countries with minimal payment:', err)
      }
    }

    if (companyId) {
      fetchCountriesWithMinimalPayment()
    }
  }, [companyId])

  const [isLoading, setIsLoading] = React.useState(false)

  const initialStartTime = React.useMemo(() => {
    if (events.length > 0) {
      const beginEvent = events.find(
        (e: any) => e.tripEvent === TripEventType.Begin,
      )
      if (beginEvent?.date) {
        return moment(beginEvent.date).startOf('day')
      }
      if (events[0]?.date) {
        return moment(events[0].date).startOf('day')
      }
    }
    if (tripData?.startDate) {
      return moment(tripData.startDate).startOf('day')
    }
    return moment().startOf('day')
  }, [events, tripData])

  const [startTime, setStartTime] = React.useState<Moment>(initialStartTime)
  const startTimeInitializedRef = React.useRef(false)

  // Only sync initialStartTime → startTime ONCE (on first valid computation)
  React.useEffect(() => {
    if (startTimeInitializedRef.current) return
    if (initialStartTime && initialStartTime.isValid()) {
      startTimeInitializedRef.current = true
      setStartTime(initialStartTime)
    }
  }, [initialStartTime])

  const dateFrom = React.useMemo(() => {
    if (events.length > 0) {
      const beginEvent = events.find(
        (e: any) => e.tripEvent === TripEventType.Begin,
      )
      if (beginEvent?.date) {
        return moment(beginEvent.date).startOf('day')
      }
      return moment(events[0].date).startOf('day')
    }
    return startTime.clone()
  }, [events, startTime])

  const dateTo = React.useMemo(() => {
    if (events.length > 0) {
      const finishEvent = events.find(
        (e: any) => e.tripEvent === TripEventType.Finish,
      )
      if (finishEvent?.date) {
        return moment(finishEvent.date).endOf('day')
      }
      return moment(events[events.length - 1].date).endOf('day')
    }
    return startTime.clone().add(7, 'days')
  }, [events, startTime])

  const initialFetchDoneRef = React.useRef(false)

  React.useEffect(() => {
    if (!companyId || !driverId) return

    initialFetchDoneRef.current = false
  }, [companyId, driverId])
  const [data, setData] = React.useState<ActivityInfo | null>(null)
  const { getDriverActivity } = useDriverActivityService()

  React.useEffect(() => {
    if (!companyId || !driverId || !startTime || !startTime.isValid()) return
    if (initialFetchDoneRef.current) return
    initialFetchDoneRef.current = true

    const fetchInitialActivities = async () => {
      setIsLoading(true)
      try {
        const days = 31
        const adjustedStart = startTime.clone().subtract(days, 'days')
        const adjustedEnd = startTime.clone().add(days, 'days')
        const data = await getDriverActivity({
          companyId: companyId,
          driverId: Number(driverId),
          startTime: adjustedStart.format('YYYY-MM-DD'),
          endTime: adjustedEnd.format('YYYY-MM-DD'),
          includeTrips: true,
        })

        setData(data)
      } catch (err) {
        console.error('Error fetching driver activities:', err)
      } finally {
        setIsLoading(false)
      }
    }

    fetchInitialActivities()
  }, [companyId, driverId, startTime])

  const handleGetDriverActivities = React.useCallback(
    async (
      companyIdParam: string,
      driverIdParam: string,
      fetchStartTime: Moment,
      fetchEndTime: Moment,
    ): Promise<any> => {
      try {
        const data = await getDriverActivity({
          companyId: companyIdParam,
          driverId: Number(driverIdParam),
          startTime: fetchStartTime.format('YYYY-MM-DD'),
          endTime: fetchEndTime.format('YYYY-MM-DD'),
          includeTrips: true,
        })
        setData(data)
      } catch (err) {
        console.error('Error fetching driver activities:', err)
        return []
      }
    },
    [getDriverActivitiesForTrip],
  )

  const sortAndReassignLp = React.useCallback(
    (evts: EventModel[]): EventModel[] => {
      const sorted = [...evts].sort((a, b) => {
        if (a.date < b.date) return -1
        if (a.date > b.date) return 1
        return 0
      })
      return sorted.map((e, index) => ({ ...ensureUid(e), lp: index }))
    },
    [],
  )

  const handleAddEvent = React.useCallback(
    (event: EventModel) => {
      const newEvents = sortAndReassignLp(
        addTripEvent(events, ensureUid(event), data?.activities),
      )
      onUpdate('events', newEvents)
    },
    [events, data?.activities, onUpdate, sortAndReassignLp],
  )

  const handleEditEvent = React.useCallback(
    (event: EventModel, isLocalChange?: boolean) => {
      const newEvents = sortAndReassignLp(
        editTripEvent(events, ensureUid(event), data?.activities),
      )
      onUpdate('events', newEvents)
    },
    [events, data?.activities, onUpdate, sortAndReassignLp],
  )

  const handleRemoveEvents = React.useCallback(
    (eventsToRemove: Array<EventModel>) => {
      const newEvents = sortAndReassignLp(
        removeTripEvents(events, eventsToRemove),
      )
      onUpdate('events', newEvents)
    },
    [events, onUpdate, sortAndReassignLp],
  )

  const handleMoveEvent = React.useCallback(
    (event: EventModel, up: boolean) => {
      const currentIndex = events.findIndex((e: any) => matchEvent(e, event))
      if (currentIndex === -1) return

      const newIndex = up ? currentIndex - 1 : currentIndex + 1
      if (newIndex < 0 || newIndex >= events.length) return

      const reordered = [...events]
      const temp = reordered[currentIndex]
      reordered[currentIndex] = reordered[newIndex]
      reordered[newIndex] = temp

      const newEvents = reordered.map((e: any, index: number) => ({
        ...e,
        lp: index,
      }))
      onUpdate('events', newEvents)
    },
    [events, onUpdate],
  )

  const handleGenerateTrips = React.useCallback(
    async (generationMode: number) => {
      if (!companyId || !driverId) return

      try {
        setIsLoading(true)
        const result = await generateTrips({
          companyId,
          driverId: Number(driverId),
          defaultStartDate: dateFrom,
          defaultEndDate: dateTo,
          events,
          generationMode,
        })

        if (result.length > 0) {
          onUpdate('events', sortAndReassignLp([...events, ...result]))
          toast.info({
            text: t('businessTrip.trips-generated'),
            title: t('businessTrip.events-generated-count').replace(
              '{0}',
              String(result.length),
            ),
          })
        } else {
          const modeKey = TripGenerationMode[generationMode]
          const modeTitleKey = `groupedRibbon.${modeKey.charAt(0).toLowerCase() + modeKey.slice(1)}Title`
          const modeTitle = t(modeTitleKey) || modeKey
          toast.warning({
            text: t('newDelegation.trips-not-generated-title'),
            title: t('newDelegation.trips-not-generated-details').replace(
              '{0}',
              modeTitle,
            ),
          })
        }
      } catch (err) {
        console.error('Error generating trips:', err)
        toast.error({ text: t('businessTrip.generate-failed') })
      } finally {
        setIsLoading(false)
      }
    },
    [
      companyId,
      driverId,
      dateFrom,
      dateTo,
      events,
      onUpdate,
      sortAndReassignLp,
    ],
  )

  const handleRestClick = React.useCallback(
    (rest: ActivityModel) => {
      const next = toggleRestOnBaseEvents({
        events,
        rest,
        activities: data?.activities || [],
        country: tripData?.destinationCountry || HOMELAND,
      })
      if (!next) return

      onUpdate('events', ensureUids(next))
    },
    [events, data?.activities, tripData?.destinationCountry, onUpdate],
  )

  const handleStartTimeChange = React.useCallback(
    (date: Moment) => {
      if (isOpenSettings || isOpenSummary) return
      setStartTime(date)
    },
    [isOpenSettings, isOpenSummary],
  )

  const filteredExistingDelegations = React.useMemo(() => {
    return data?.existingDelegations.filter(
      (d: any) => d.delegationNumber !== tripData?.number,
    )
  }, [data?.existingDelegations, tripData?.number])

  return (
    <DndRoot backend={HTML5Backend}>
      <div className="tw-flex tw-h-full tw-flex-col">
        <BusinessTripActionBar
          events={events}
          isRestsOnBaseModeOn={isRestsOnBaseModeOn}
          setIsRestsOnBaseModeOn={setIsRestsOnBaseModeOn}
          onShowGpsView={onShowGpsView}
          onShowTmsView={onShowTmsView}
          onShowTachographView={onShowTachographView}
          onShowCardLoads={onShowCardLoads}
          onShowBorders={() => setShowBordersView(true)}
          onGenerateTrips={handleGenerateTrips}
        />
        <BusinessTripChartNavigation
          startTime={startTime}
          setStartTime={setStartTime}
        />
        <CustomDragLayer />
        <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-y-auto tw-bg-white">
          <div className="tw-flex-1 tw-overflow-hidden">
            <BusinessTripChartWrapperNew
              companyId={companyId}
              driverId={Number(driverId)}
              tripId={tripId}
              readonly={false}
              groupMode={false}
              dateFrom={dateFrom}
              dateTo={dateTo}
              events={events}
              isRestsOnBaseModeOn={isRestsOnBaseModeOn}
              onRestClick={handleRestClick}
              addEvent={handleAddEvent}
              removeEvents={handleRemoveEvents}
              editEvent={handleEditEvent}
              moveEvent={handleMoveEvent}
              hideDelegationMarkerPopups={() => {}}
              markNewEvent={() => {}}
              activities={data?.activities}
              breaks={data?.breaks}
              cardInserts={data?.cardInserts}
              dailyPartialRegularRests={data?.dailyPartialRegularRests}
              dailyRests={data?.dailyRests}
              dayActivities={data?.dayActivities}
              outOfScopes={data?.outOfScopes}
              existingDelegations={filteredExistingDelegations}
              tripEvents={data?.tripEvents}
              partialBreaks={data?.partialBreaks}
              places={data?.places}
              violations={data?.violations}
              weekRecompenses={data?.weekRecompenses}
              weeklyReducedRests={data?.weeklyReducedRests}
              weeklyRests={data?.weeklyRests}
              accomodations={
                tripData?.accommodations || tripData?.accomodations
              }
              countries={countries as any}
              countriesWithMinimalPayment={countriesWithMinimalPayment}
              getDriverActivities={handleGetDriverActivities}
              isLoading={isLoading}
              number={tripData?.number}
              translate={(text: string) => t(text) || text}
              startTime={startTime}
              onStartTimeChange={handleStartTimeChange}
              noDndProvider={true}
            />
          </div>
        </div>
      </div>
    </DndRoot>
  )
}

export default BusinessTripEditActivityChart
